import { component$ } from "@builder.io/qwik";
import type { DocumentHead, DocumentHeadProps } from "@builder.io/qwik-city";
import { routeLoader$ } from "@builder.io/qwik-city";
import { getCoachesByCategory } from "~/lib/coach-grouping";
import { getCategoryPageContent } from "~/lib/coach-group-seo.db";
import {
  getCategoryBySlug,
  getSubcategoriesByCategorySlug,
} from "~/lib/expertise.db";
import Heading from "~/components/ui/heading";
import { CoachCard } from "~/components/coach-card";
import Subheading from "~/components/ui/subheading";
import { BreakdownIntro } from "~/components/feature/breakdown-intro";
import { MidListCTA } from "~/components/feature/mid-list-cta";
import { BottomCTA } from "~/components/feature/bottom-cta";
import { BlogGrid } from "~/components/blog-grid";
import { MarkdownContent } from "~/components/markdown-content";
import { FaqList } from "~/components/faq-list";

export const useCategoryCoaches = routeLoader$(async (event) => {
  const { categorySlug } = event.params;
  const [coaches, category, page] = await Promise.all([
    getCoachesByCategory(categorySlug),
    getCategoryBySlug(categorySlug),
    getCategoryPageContent(categorySlug),
  ]);
  const subcategories = await getSubcategoriesByCategorySlug(categorySlug);
  return { coaches, category, subcategories, categorySlug, page };
});

export default component$(() => {
  const { coaches, category, subcategories, categorySlug, page } =
    useCategoryCoaches().value;
  const name = category?.listingName || category?.name || "";

  return (
    <div class="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <Heading
        preHeadline="Dream Coach Match"
        headline={
          name ? `Looking for a ${name} Coach?` : "Looking for a Coach?"
        }
        subheadline={
          name
            ? `Discover expert ${name} coaches. Whether you're seeking personal growth, skill development, or specific ${name} expertise, find the right coach to guide you.`
            : "Discover expert coaches. Whether you're seeking personal growth, skill development, or specific expertise, find the right coach to guide you."
        }
      />

      <BreakdownIntro
        breakdownName={name}
        breakdownTypeSlug={categorySlug}
        ctaButtonTextTemplate="Find Your Dream {} Coach"
        utmCampaignPrefix="category_coaches"
      />

      {subcategories && subcategories.length > 0 && (
        <div class="my-12">
          <Subheading class="text-center">
            Explore {name} Subcategories
          </Subheading>
          <div class="flex flex-wrap justify-center gap-4">
            {subcategories.map((sub) => (
              <a
                key={sub.id}
                href={`/coaches/subcategory/${sub.slug}/`}
                class="bg-brand-100 text-brand-700 hover:bg-brand-200 shadow-brand-800/15 border-brand-300 rounded-lg border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-150 ease-in-out hover:shadow-md"
              >
                {sub.name}
              </a>
            ))}
          </div>
        </div>
      )}

      <div class="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {coaches.length > 0 ? (
          coaches.map((coach, i) => (
            <>
              <CoachCard
                key={coach.id}
                coachData={{
                  coach: coach,
                  profile: coach,
                }}
              />
              {i === 11 && (
                <MidListCTA
                  breakdownName={name}
                  breakdownTypeSlug={categorySlug}
                  preLinkText="Not sure which coach to choose?"
                  ctaLinkTextTemplate="Let us match you with the perfect {} coach!"
                  utmCampaignPrefix="category_coaches_mid"
                />
              )}
            </>
          ))
        ) : (
          <p class="col-span-full text-center text-lg text-gray-500">
            No coaches found in this category yet.
          </p>
        )}
      </div>

      <BottomCTA
        itemCount={coaches.length}
        itemTypePlural="coaches"
        breakdownName={name}
        breakdownTypeSlug={categorySlug}
        joinAsLinkUrl="/page/coach"
        joinAsLinkText="Join as a coach."
        mainCtaSubTextTemplate="Ready to find your dream {} coach?"
        mainCtaButtonTextTemplate="Try our free {} Matchmaker tool"
        utmCampaignPrefix="category_coaches_bottom"
      />

      <div class="mx-auto max-w-3xl">
        <BlogGrid
          limit={2}
          class="mt-8"
          query={{ "data.tags": { $elemMatch: { $eq: categorySlug } } }}
          heading="Useful Related Articles"
        />
      </div>

      {page?.contentMd && (
        <div class="prose mx-auto mt-12">
          <MarkdownContent content={page.contentMd} />
        </div>
      )}
      {page?.faqItems && page.faqItems.length > 0 && (
        <FaqList items={page.faqItems} class="mx-auto max-w-2xl" />
      )}
    </div>
  );
});

export const head: DocumentHead = (props: DocumentHeadProps) => {
  const { params, resolveValue } = props;
  const { category, page } = resolveValue(useCategoryCoaches);
  const name = category?.listingName || category?.name || params.categorySlug;
  return {
    title: page?.seoTitle ?? `${name} Coaches | Dream Coach Match`,
    meta: page?.metaDescription
      ? [
          {
            name: "description",
            content: page.metaDescription,
          },
        ]
      : [],
  };
};
